import axios from 'axios';
import { USE_MOCKS } from '@/lib/mocks';

declare module 'axios' {
  export interface AxiosRequestConfig {
    /**
     * Manda a requisição **sem** `Authorization`, mesmo havendo sessão.
     *
     * Existe por uma rota: o cadastro anônimo, que leva e-mail e senha no
     * corpo e cria a identidade no servidor. Com um token junto, o back leria
     * a chamada como a forma autenticada e recusaria os dois campos (400).
     */
    anonymous?: boolean;
  }
}

/**
 * Cliente da API do Primum Core (`VITE_API_BASE_URL`), com o `x-scope-id`
 * desta plataforma em toda requisição: é por ele que o back resolve o perfil
 * da pessoa (`ScopedProfileGuard`) e confere que o envio é daqui.
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'x-scope-id': import.meta.env.VITE_SCOPE_ID,
  },
});

apiClient.interceptors.request.use(async (config) => {
  // Mock não tem Firebase; anônimo não leva token, nem espera o restore.
  if (USE_MOCKS || config.anonymous) return config;

  // Import tardio: o Firebase só é carregado quando há API real de verdade.
  const { firebaseAuth } = await import('@/lib/firebase');
  // Na primeira requisição depois de um reload, `currentUser` ainda pode ser
  // `null` enquanto o Firebase restaura a sessão salva.
  await firebaseAuth.authStateReady();

  const user = firebaseAuth.currentUser;
  if (user) {
    // Token em cache enquanto válido, renovado sozinho perto de expirar:
    // nunca guardamos o token por conta própria.
    config.headers.set('Authorization', `Bearer ${await user.getIdToken()}`);
  }
  return config;
});

/** O `code` do erro do Primum Core (`{statusCode, error, message}`), se houver. */
export function apiErrorCode(error: unknown): string | undefined {
  if (!axios.isAxiosError(error)) return undefined;
  const data = error.response?.data as { error?: unknown } | undefined;
  return typeof data?.error === 'string' ? data.error : undefined;
}

export function apiErrorStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}
